import {Component, computed, inject, linkedSignal, resource, signal} from '@angular/core';
import {FormsModule} from '@angular/forms'; // ← needed for ngModel
import {TranslocoPipe} from '@jsverse/transloco';
import {RadioButtonModule} from 'primeng/radiobutton';
import {Textarea} from 'primeng/textarea';
import {firstValueFrom, of} from 'rxjs';

import {ArchitecturalPrincipleDirectus, DirectusService} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {
  ArchitecturalPrincipleControllerService,
  ArchitecturalPrincipleResponse,
  GetByProjectRequestParams,
  SetRequestParams,
} from '@frontend/shared-api';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {FixedValueService} from '../../../../../../shared/services/fixed-value.service';
import {NoDataComponent} from '../../../../../../shared/components/no-data/no-data.component';

@Component({
  selector: 'app-step-2-2-architectural-principles',
  imports: [
    FormsModule,
    TranslocoPipe,
    RadioButtonModule,
    Textarea,
    ProjectStepSaveButtonComponent,
    TranslateDirectusPipe,
    LoadingComponent,
    NoDataComponent,
  ],
  templateUrl: './step-2-2-architectural-principles.component.html',
})
export class Step22ArchitecturalPrinciplesComponent extends ProjectBaseStepComponent {
  private readonly directusService = inject(DirectusService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly architecturalPrincipleController = inject(
    ArchitecturalPrincipleControllerService,
  );
  private readonly toast = inject(ToastService);

  private readonly collectionName = 'architectural_principle';

  saving = signal(false);

  private readonly optionsResource = resource({
    params: () => this.projectId,
    loader: ({params: projectId}) => this.loadSelectableOptions(projectId),
  });

  private readonly storedResource = resource({
    params: () => this.projectId,
    loader: ({params: projectId}) => this.loadStoredPrinciple(projectId),
  });

  isLoading = computed(() => this.optionsResource.isLoading() || this.storedResource.isLoading());
  options = computed(() => this.optionsResource.value() ?? []);
  selected = linkedSignal(() => this.storedResource.value()?.name ?? null);
  explanation = linkedSignal(() => this.storedResource.value()?.explanation ?? '');
  touched = signal(false);
  canSave = computed(() => this.touched() && this.selected() !== null);

  onSelect(value: string) {
    this.touched.set(true);
    this.selected.set(value);
  }

  onExplanationChange(value: string) {
    this.touched.set(true);
    this.explanation.set(value);
  }

  async onSave() {
    const name = this.selected();
    if (!name) return;

    const request: SetRequestParams = {
      projectId: this.projectId,
      architecturalPrinciplesUpsertRequest: {
        name,
        explanation: this.explanation().trim() || undefined,
      },
    };

    this.saving.set(true);
    try {
      await firstValueFrom(this.architecturalPrincipleController.set(request));
      this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
      this.stepState.loadStepStatuses(this.projectId);
      this.touched.set(false);
    } finally {
      this.saving.set(false);
    }
  }

  private async loadSelectableOptions(
    projectId: number,
  ): Promise<ArchitecturalPrincipleDirectus[]> {
    const principles = await firstValueFrom(this.directusService.loadArchitecturalPrinciples());
    const storedIds = await firstValueFrom(
      this.fixedValueService.loadOrInitializeFixedValues(projectId, this.collectionName, () =>
        of(principles),
      ),
    );

    return principles.filter(
      (principle) => storedIds.length === 0 || storedIds.includes(principle.id),
    );
  }

  private async loadStoredPrinciple(
    projectId: number,
  ): Promise<ArchitecturalPrincipleResponse | null> {
    const request: GetByProjectRequestParams = {projectId};
    const stored = await firstValueFrom(
      this.architecturalPrincipleController.getByProject(request),
    );

    return stored[0] ?? null;
  }
}
